import React, { FC } from 'react';
import { KeywordsTemplateProps } from '.';
import {
    useGetModelKeywords,
    useGetOfferKeywords,
    useGetPaymentKeywords,
} from '@components/OfferListItem/utils/getOfferKeywords';
import { usePrices } from '@hooks/usePriceCalculator';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { getMonthlyPriceObjByType } from '@utils/Price.utils';
import { StockFormatedPriceType } from '../../services/stock/models/stockItem.price.model';
import { findDefaultConfiguration } from '@utils/Offers.utils';
import { PaymentJourneyTypes } from '../../partExchange/interfaces/Default';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../context/featureSwitchApp';
import { useJourneyType } from '@hooks/useJourneyType';
import useTranslations from '@hooks/useTranslations';
import { ICalculateSummaryRequestData } from '../../services/calculateSummary/calculateSummary.types';
import { useSummaryDataPromise } from '@components/OfferListItem/hooks/useSummaryDataPromise';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';

const KeywordsTemplate: FC<KeywordsTemplateProps> = ({ className }) => {
    const { t } = useTranslations();
    const { carDetails } = useCarDetailsDuck();
    const { paymentJourneyType } = useJourneyType();
    const { isStockJourney } = useCarStockJourneyCheck();

    const { cash, finance } = usePrices(carDetails, true, null, true);

    const {
        cash: { totalPrice: defaultTotalPrice },
        finance: { totalPrice: defaultMonthlyPrice },
    } = usePrices(carDetails, true, null, false);

    const { totalPrice: baseTotalPrice } = cash;
    const {
        totalPrice: baseMonthlyPrice,
        financeProperties: { deposit, duration, annualMileage, apr, hasMonthlyPrices },
    } = finance;

    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);
    const isBenefitsEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BENEFITS_MODE_ENABLED);

    const defaultConfiguration = findDefaultConfiguration(carDetails?.offers, paymentJourneyType as PaymentJourneyTypes);
    const allowDefaultConfiguration = isBenefitsEnabled && defaultConfiguration;

    const amountRounding = getMonthlyPriceObjByType(carDetails?.prices, StockFormatedPriceType.EMPLOYEE)?.amountRounding;
    const paymentKeywords = useGetPaymentKeywords(deposit, duration, annualMileage, apr);
    const modelKeywords = useGetModelKeywords(
        carDetails,
        allowDefaultConfiguration && defaultConfiguration,
        allowDefaultConfiguration ? defaultTotalPrice : baseTotalPrice,
        allowDefaultConfiguration ? defaultMonthlyPrice : baseMonthlyPrice,
        null,
        amountRounding
    );
    const offerKeywords = useGetOfferKeywords(carDetails);

    const carConfigId = isStockJourney ? carDetails?.externalId : carDetails?._id;
    const params: ICalculateSummaryRequestData = isStockJourney
        ? { carConfigId, carConfiguration: carDetails }
        : { carConfigId };

    const { processCalculateSummaryDataPromise } = useSummaryDataPromise(params);

    return (
        <div className={className} data-testid="TESTING_TRIM_ITEM_KEYWORDS">
            {(!isSOLEnabled && offerKeywords) &&
                <div className="offer">{offerKeywords}</div>
            }
            {hasMonthlyPrices &&
                <div className="payment">
                    {isSOLEnabled && <span className="payment-description">{paymentKeywords}</span>}
                    {isSOLEnabled && (
                        <button
                            className="payment-link"
                            onClick={processCalculateSummaryDataPromise}
                        >
                            {t('label.link.finance')}
                        </button>
                    )}
                </div>
            }
            <div className="model">
                {modelKeywords}
            </div>
        </div>
    );
};

export default KeywordsTemplate;
